import { useCallback, useId, useMemo, type ReactNode } from "react";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router";
import { GnbMenu } from "@/app/shell/Gnb";
import { PageBase, type PageBaseProps } from "@/app/shell/PageBase";
import { wouldLoseDatasetMappingEdits } from "@/modules/inspect/public";
import { DiscardEditsModal } from "@/shared/ui/window";
import { wouldLoseDatasetFactsEdits } from "../feature/datasetFactsEditor";
import {
  DatasetDetailController,
  type DatasetDetailSnapshot,
} from "../feature/datasetDetailController";
import {
  describeDatasetSections,
  readDatasetSection,
  writeDatasetSection,
  type DatasetSection,
} from "../feature/datasetSections";
import { datasetDetailTitleBadge } from "./components/datasetDetailBadge";
import { DatasetDetailActions } from "./components/DatasetDetailActions";
import { BackToListAction, DatasetDetailBody } from "./components/DatasetDetailBody";
import { DatasetSectionTabs } from "./components/DatasetSectionTabs";
import { datasetProbePath, datasetsListPath, isDatasetDetailPath } from "./routes";
import styles from "./DatasetDetailPage.module.css";

/**
 * 딥링크용 route 페이지. 목록에서는 같은 본문을 전체화면 창으로 연다(`DatasetDetailWindowContent`).
 *
 * 섹션은 `?section=` 이, 고른 Probe 는 경로가 소유한다 — 공유한 링크가 같은 자리를 연다. 경로에
 * uuid 가 없으면 이력의 첫 건(가장 최근)을 고른다.
 *
 * 떠나면 잃는 편집이 있으면 떠나기 전에 묻는다(`DatasetLeaveGuard`). 창의 닫기 guard 와 같은 문구다.
 */
export function DatasetDetailPage() {
  const { datasetUuid, probeUuid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const section = useMemo(() => readDatasetSection(searchParams), [searchParams]);
  const idPrefix = useId();

  const handleSectionChange = useCallback(
    (next: DatasetSection) => {
      setSearchParams((prev) => writeDatasetSection(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  const handleOpenProbe = useCallback(
    (nextProbeUuid: string, nextSection: DatasetSection) => {
      const params = writeDatasetSection(new URLSearchParams(), nextSection).toString();
      const path = datasetProbePath(datasetUuid ?? "", nextProbeUuid);
      navigate(params === "" ? path : `${path}?${params}`, { replace: true });
    },
    [datasetUuid, navigate],
  );

  const handleBackToList = useCallback(() => navigate(datasetsListPath), [navigate]);

  if (datasetUuid === undefined) {
    return null;
  }

  return (
    <DatasetDetailPageView
      currentMenu={GnbMenu.Datasets}
      datasetUuid={datasetUuid}
      idPrefix={idPrefix}
      key={datasetUuid}
      pageTitle="Dataset"
      probeUuid={probeUuid ?? null}
      section={section}
      showBackButton
      onBackToList={handleBackToList}
      onOpenProbe={handleOpenProbe}
      onSectionChange={handleSectionChange}
    />
  );
}

type DatasetDetailPageViewProps = PageBaseProps & {
  datasetUuid: string;
  probeUuid: string | null;
  section: DatasetSection;
  idPrefix: string;
  onSectionChange: (section: DatasetSection) => void;
  onOpenProbe: (probeUuid: string, section: DatasetSection) => void;
  onBackToList: () => void;
};

type DatasetDetailPageViewState = {
  snapshot: DatasetDetailSnapshot;
};

class DatasetDetailPageView extends PageBase<
  DatasetDetailPageViewProps,
  DatasetDetailPageViewState
> {
  // 페이지 수명 동안 사는 인스턴스 자원 — 조회 · 폴링 · 모달 상태를 든다.
  private readonly controller = new DatasetDetailController({
    datasetUuid: this.props.datasetUuid,
    // 접수로 생긴 Probe 는 경로에 남기고 Probe 섹션으로 옮긴다.
    onOpenProbe: (probeUuid) => this.props.onOpenProbe(probeUuid, "probes"),
  });

  private unsubscribe: (() => void) | null = null;

  state: DatasetDetailPageViewState = { snapshot: this.controller.getSnapshot() };

  protected override async initPage(): Promise<void> {
    this.unsubscribe = this.controller.subscribe(() => {
      this.setState({ snapshot: this.controller.getSnapshot() });
    });
    this.controller.setCurrentProbe(this.props.probeUuid);
    await this.controller.init();
  }

  protected override disposePage(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.controller.dispose();
  }

  override componentDidUpdate(previous: DatasetDetailPageViewProps): void {
    if (previous.probeUuid !== this.props.probeUuid) {
      this.controller.setCurrentProbe(this.props.probeUuid);
    }
  }

  protected override renderTitleAside(): ReactNode {
    return datasetDetailTitleBadge(this.state.snapshot.dataset, this.controller.currentProbe());
  }

  // 섹션 탭은 제목 줄 아래 · 본문(tabpanel) 밖이다 — 탭을 옮겨도 남아야 하고, 본문이 자기
  // tabpanel 을 소유해 창 셸과 같은 배선을 쓴다(그래서 `PageBase` 의 tabs prop 은 쓰지 않는다).
  protected override renderBelowTabs(): ReactNode {
    return (
      <DatasetSectionTabs
        entries={describeDatasetSections(this.state.snapshot.dataset)}
        active={this.props.section}
        idPrefix={this.props.idPrefix}
        panelId={`${this.props.idPrefix}-panel`}
        onSelect={this.props.onSectionChange}
      />
    );
  }

  protected override renderHeaderActions(): ReactNode {
    const { dataset } = this.state.snapshot;
    if (dataset === null) {
      return null;
    }
    return (
      <DatasetDetailActions
        dataset={dataset}
        onCopySourceRef={this.controller.copySourceRef}
        onHide={this.controller.openHideDialog}
        onRequestProbe={this.controller.openRequestProbeDialog}
        onShow={this.controller.show}
      />
    );
  }

  protected override renderContent(): ReactNode {
    return (
      <div className={styles.content}>
        <DatasetDetailBody
          active
          controller={this.controller}
          idPrefix={this.props.idPrefix}
          notFoundAction={<BackToListAction onBackToList={this.props.onBackToList} />}
          section={this.props.section}
          onOpenProbe={this.props.onOpenProbe}
        />
        <DatasetLeaveGuard
          datasetUuid={this.props.datasetUuid}
          title={this.state.snapshot.dataset?.displayName ?? "Dataset"}
        />
      </div>
    );
  }
}

/**
 * 상세 route 를 떠나기 전에 묻는다. 이 페이지가 놓으면 잃는 매핑 · 조사 사실 편집이 있을 때만이다 — 같은 Dataset 을
 * 창도 잡고 있으면 편집은 그쪽에 남는다. 같은 상세 안의 이동(섹션 탭의 `?section=` · 고른 Probe 경로)은 막지 않는다.
 * 브라우저 뒤로 · 앞으로도 막는다. 문구와 버튼은 창의 닫기 guard 와 같다.
 *
 * 세션 끝은 묻지 않는다(창 스택의 `clear` 와 같다). 인증 게이트가 이 페이지를 먼저 내리고 이동하기 때문이다.
 */
function DatasetLeaveGuard({ datasetUuid, title }: { datasetUuid: string; title: string }) {
  const blocker = useBlocker(
    ({ nextLocation }) =>
      !isDatasetDetailPath(nextLocation.pathname, datasetUuid) &&
      (wouldLoseDatasetMappingEdits(datasetUuid) || wouldLoseDatasetFactsEdits(datasetUuid)),
  );
  if (blocker.state !== "blocked") return null;
  return (
    <DiscardEditsModal
      heading="페이지 나가기"
      message={`${title} 페이지에 저장하지 않은 편집이 있습니다. 페이지를 나가면 편집을 잃습니다.`}
      confirmText="편집 버리고 나가기"
      onCancel={() => blocker.reset()}
      onConfirm={() => blocker.proceed()}
    />
  );
}
